// ── Settings.js ──────────────────────────────────────────────────────
// Full-screen Settings overlay. Open via TopBar settings icon.
// Sections: Profile · Game Preferences · Custom Content · Data & Account · About · Danger Zone
//
// Customs are auto-detected from Firestore: customBrands, customGMs, customRosters,
// official wrestlers (filtered by isCustom === true), and per-brand customTitles.
// Usage detection: scans the user's gameSlots saves to find references.

import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, setDoc, deleteDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAccess } from '../useAccess';
import CustomWrestlerEditor from './CustomWrestlerEditor';
import AdminDevPanel from './AdminDevPanel';
import { CURRENT_VERSION } from '../patchNotesDatabase';
import { TICKET_STATUS_MAP, TICKET_CATEGORY_LABELS } from './ticketsDatabase';

const placeholder = process.env.PUBLIC_URL + '/placeholder.png';

const APP_VERSION = 'v' + CURRENT_VERSION;
const APP_BUILD = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().slice(0, 10).replace(/-/g, '.');

// ── Shared styles ────────────────────────────────────────────────────
const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

// ── ICONS (inline SVG, no emoji) ─────────────────────────────────────
const IconClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const IconChevron = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// ── Toggle switch ────────────────────────────────────────────────────
const Toggle = ({ on, onChange, disabled }) => (
  <div onClick={() => !disabled && onChange && onChange(!on)}
    style={{
      position: 'relative',
      width: '44px', height: '22px',
      background: on ? 'rgba(46,204,113,0.25)' : 'rgba(255,255,255,0.08)',
      border: '1px solid ' + (on ? 'rgba(46,204,113,0.6)' : 'rgba(255,255,255,0.12)'),
      borderRadius: '11px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'all 0.2s',
      flexShrink: 0,
    }}>
    <div style={{
      position: 'absolute', top: '2px', left: on ? '24px' : '2px',
      width: '16px', height: '16px',
      background: on ? '#2ecc71' : 'rgba(255,255,255,0.4)',
      borderRadius: '50%',
      transition: 'all 0.2s',
    }} />
  </div>
);

// ── Section wrapper ──────────────────────────────────────────────────
const Section = ({ num, title, danger, custom, children, headerExtra, description }) => (
  <div style={{
    background: 'rgba(8,8,8,0.92)',
    border: '1px solid ' + (danger ? 'rgba(231,76,60,0.25)' : custom ? 'rgba(212,175,55,0.18)' : 'rgba(255,255,255,0.08)'),
    borderRadius: '10px',
    padding: '24px 28px',
    marginBottom: '20px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
  }}>
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      marginBottom: '18px', paddingBottom: '12px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ ...S.anton, fontSize: '16px', letterSpacing: '4px', color: danger ? '#e74c3c' : '#fff' }}>{title}</div>
      <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '2px', color: 'rgba(255,255,255,0.25)' }}>{num}</div>
    </div>
    {description && (
      <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.5px', marginBottom: '16px', lineHeight: 1.6 }}>
        {description}
      </div>
    )}
    {children}
  </div>
);
// ── My Tickets list (read-only, player-facing) ────────────────────────
function MyTicketsList({ user, onSeen }) {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const q = query(collection(db, 'tickets'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
        setTickets(list);
        await updateDoc(doc(db, 'users', user.uid), { lastSeenTicketsAt: serverTimestamp() });
        onSeen && onSeen();
      } catch (e) {
        console.error('My tickets fetch error:', e);
        setTickets([]);
      }
    })();
  }, [user]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (tickets === null) {
    return <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', padding: '8px 0' }}>Loading your tickets...</div>;
  }
  if (tickets.length === 0) {
    return <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.3)', padding: '8px 0' }}>You haven't submitted any ticket yet.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {tickets.map(t => {
        const meta = TICKET_STATUS_MAP[t.status] || TICKET_STATUS_MAP.open;
        const isOpen = openId === t.id;
        return (
          <div key={t.id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', overflow: 'hidden' }}>
            <div onClick={() => setOpenId(isOpen ? null : t.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', cursor: 'pointer' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...S.anton, fontSize: '11px', letterSpacing: '0.5px', color: '#fff' }}>{t.title}</div>
                <div style={{ ...S.mono, fontSize: '9px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                  {TICKET_CATEGORY_LABELS[t.category] || t.category} · {formatDate(t.createdAt)}
                </div>
              </div>
              <span style={{ ...S.mono, fontSize: '9px', fontWeight: 'bold', letterSpacing: '0.5px', padding: '3px 10px', borderRadius: '10px', color: meta.color, border: '1px solid ' + meta.color + '55', background: meta.color + '18', whiteSpace: 'nowrap' }}>
                {meta.label}
              </span>
            </div>
            {isOpen && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '12px 14px' }}>
                <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: '4px' }}>Description</div>
                <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{t.description}</div>
                {t.stepsToReproduce && (
                  <>
                    <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', margin: '10px 0 4px' }}>Steps to reproduce</div>
                    <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{t.stepsToReproduce}</div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Row component ────────────────────────────────────────────────────
const Row = ({ label, desc, children, comingSoon }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    gap: '20px',
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ ...S.mono, fontSize: '12px', letterSpacing: '1px', color: 'rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center' }}>
        {label}
        {comingSoon && (
          <span style={{ ...S.mono, fontSize: '8px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', padding: '3px 7px', borderRadius: '3px', marginLeft: '8px' }}>SOON</span>
        )}
      </div>
      {desc && <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.35)', marginTop: '3px' }}>{desc}</div>}
    </div>
    <div style={{ flexShrink: 0 }}>{children}</div>
  </div>
);

// ── Action button ────────────────────────────────────────────────────
const ActionBtn = ({ children, onClick, variant = 'primary', disabled }) => {
  const styles = {
    primary: { background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' },
    secondary: { background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' },
    danger: { background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', color: '#e74c3c' },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        ...S.mono, fontSize: '10px', letterSpacing: '1.5px',
        ...styles[variant],
        padding: '7px 16px', borderRadius: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}>
      {children}
    </button>
  );
};

// ── Custom Content Manager ───────────────────────────────────────────
const CUSTOM_TABS = [
  { key: 'brands', label: 'BRANDS' },
  { key: 'gms', label: 'GMs' },
  { key: 'wrestlers', label: 'SUPERSTARS' },
  { key: 'titles', label: 'TITLES' },
  { key: 'rosters', label: 'ROSTERS' },
];

const CustomContentManager = ({ user, gameSlots, customBrands, customGMs, customWrestlers, customRosters, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('brands');
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, item, usedIn: [] }
  const [editTarget, setEditTarget] = useState(null); // { type, item } - null when closed
  const [createMode, setCreateMode] = useState(null); // type to create
  const [deleting, setDeleting] = useState(false);

  // Aggregate custom titles from all custom brands
  const customTitles = (customBrands || [])
    .flatMap(b => (b.customTitles || []).map(t => ({ ...t, _parentBrand: b })));

  // Usage detection helpers
  const findBrandUsage = (brandName) => gameSlots.filter(s =>
    s.brand === brandName || (s.rivals || []).some(r => r.brand === brandName)
  );

  const findGMUsage = (gmName) => gameSlots.filter(s =>
    s.gm === gmName || (s.rivals || []).some(r => r.gm === gmName)
  );

  const findWrestlerUsage = (wrestlerId) => gameSlots.filter(s =>
    (s.roster || []).some(w => String(w.wrestler_id) === String(wrestlerId)) ||
    (s.rivals || []).some(r => (r.roster || []).some(w => String(w.wrestler_id) === String(wrestlerId)))
  );

  const findTitleUsage = (titleId, parentBrandName) => gameSlots.filter(s => {
    if (s.brand !== parentBrandName) return false;
    const champs = s.champions || {};
    return Object.values(champs).some(slotKey => {
      // titleHistory may also reference it
      return false; // Simplified — title is "used" if its parent brand is used
    }) || s.brand === parentBrandName;
  });

  const findRosterUsage = (rosterId) => gameSlots.filter(s =>
    s.customRosterId === rosterId || s._rosterId === rosterId
  );

  // Build list of items per tab
  const items = (() => {
    if (activeTab === 'brands') return (customBrands || []).map(b => ({
      type: 'brand', id: b._docId || b.id, name: b.name,
      thumb: b.logo, thumbBg: b.bg, color: b.color,
      meta: ((b.customTitles || []).length) + ' title' + (((b.customTitles || []).length) === 1 ? '' : 's') + ' attached',
      usedIn: findBrandUsage(b.name),
      raw: b,
    }));
    if (activeTab === 'gms') return (customGMs || []).map(g => ({
      type: 'gm', id: g._docId || g.id, name: g.name,
      thumb: g.img || g.render,
      meta: 'Custom GM',
      usedIn: findGMUsage(g.name),
      raw: g,
    }));
    if (activeTab === 'wrestlers') return (customWrestlers || []).map(w => ({
      type: 'wrestler', id: w.wrestler_id || w._docId, name: w.name,
      thumb: w.render || w.customRender,
      meta: (w.fight_class || '—') + ' · ' + (w.heel ? 'Heel' : 'Face') + (w.starting_xp ? ' · LVL ' + w.starting_xp : ''),
      usedIn: findWrestlerUsage(w.wrestler_id),
      raw: w, // _docId is included in w (set during fetchAll)
    }));
    if (activeTab === 'titles') return customTitles.map(t => ({
      type: 'title', id: t.id, name: t.name,
      thumb: t.render,
      meta: 'Brand: ' + (t._parentBrand && t._parentBrand.name) + ' · ' + (t.gender === 'F' ? 'Female' : t.gender === 'M' ? 'Male' : 'Mixed'),
      usedIn: findTitleUsage(t.id, t._parentBrand && t._parentBrand.name),
      raw: t,
    }));
    if (activeTab === 'rosters') return (customRosters || []).map(r => ({
      type: 'roster', id: r._docId || r.id, name: r.name || 'Untitled roster',
      thumb: null,
      meta: ((r.wrestlers || []).length) + ' wrestlers',
      usedIn: findRosterUsage(r._docId || r.id),
      raw: r,
    }));
    return [];
  })();

  // Counts for tabs
  const counts = {
    brands: (customBrands || []).length,
    gms: (customGMs || []).length,
    wrestlers: (customWrestlers || []).length,
    titles: customTitles.length,
    rosters: (customRosters || []).length,
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { type, item } = confirmDelete;
      if (type === 'brand') {
        if (item.id) await deleteDoc(doc(db, 'customBrands', item.id));
      } else if (type === 'gm') {
        if (item.id) await deleteDoc(doc(db, 'customGMs', item.id));
      } else if (type === 'wrestler') {
        // Custom wrestlers are stored in officialWrestlers with isCustom: true
        // Find and delete the doc
        const q = query(collection(db, 'officialWrestlers'), where('wrestler_id', '==', item.raw.wrestler_id), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        for (const d of snap.docs) await deleteDoc(d.ref);
      } else if (type === 'title') {
        // Title is part of a brand's customTitles array — update that brand
        const parentBrand = item.raw._parentBrand;
        if (parentBrand && parentBrand._docId) {
          const newTitles = (parentBrand.customTitles || []).filter(t => t.id !== item.id);
          await updateDoc(doc(db, 'customBrands', parentBrand._docId), { customTitles: newTitles });
        }
      } else if (type === 'roster') {
        if (item.id) await deleteDoc(doc(db, 'customRosters', item.id));
      }
      setConfirmDelete(null);
      onRefresh && onRefresh();
    } catch (e) {
      console.error('Delete failed:', e);
      alert('Delete failed: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '18px', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
        {CUSTOM_TABS.map(t => {
          const isActive = activeTab === t.key;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{
                ...S.anton, fontSize: '11px', letterSpacing: '2px',
                background: 'transparent', border: 'none',
                borderBottom: '2px solid ' + (isActive ? '#d4af37' : 'transparent'),
                color: isActive ? '#d4af37' : 'rgba(255,255,255,0.4)',
                padding: '10px 16px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}>
              {t.label}
              <span style={{
                ...S.mono, fontSize: '9px',
                background: isActive ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.06)',
                border: '1px solid ' + (isActive ? 'rgba(212,175,55,0.4)' : 'rgba(255,255,255,0.1)'),
                color: isActive ? '#d4af37' : 'rgba(255,255,255,0.5)',
                padding: '2px 6px', borderRadius: '8px', letterSpacing: 0,
              }}>{counts[t.key]}</span>
            </button>
          );
        })}
        {/* NEW button (only for tabs that support creation) */}
        {(activeTab === 'wrestlers') && (
          <button onClick={() => setCreateMode(activeTab)}
            style={{
              ...S.anton, fontSize: '10px', letterSpacing: '2px',
              background: 'rgba(46,204,113,0.12)',
              border: '1px solid rgba(46,204,113,0.4)',
              color: '#2ecc71',
              padding: '6px 14px',
              borderRadius: '4px',
              marginLeft: 'auto',
              alignSelf: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.22)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.12)'; }}>
            + NEW
          </button>
        )}
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.3)', fontSize: '11px', letterSpacing: '1px', ...S.mono }}>
          No custom {CUSTOM_TABS.find(x => x.key === activeTab).label.toLowerCase()} yet.
        </div>
      )}

      {/* Grid */}
      {items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
          {items.map(it => (
            <CustomCard
              key={it.type + '-' + it.id}
              item={it}
              onDelete={() => setConfirmDelete({ type: it.type, item: it })}
              onEdit={it.type === 'wrestler' ? () => setEditTarget({ type: it.type, item: it }) : null}
            />
          ))}
        </div>
      )}

      {/* Wrestler editor (create or edit) */}
      {(createMode === 'wrestlers' || (editTarget && editTarget.type === 'wrestler')) && (
        <CustomWrestlerEditor
          wrestler={editTarget?.item?.raw}
          userId={user?.uid}
          onClose={() => { setCreateMode(null); setEditTarget(null); }}
          onSaved={() => { setCreateMode(null); setEditTarget(null); onRefresh && onRefresh(); }}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.75)',
          backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px',
        }}
          onClick={() => !deleting && setConfirmDelete(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background: 'rgba(8,8,8,0.98)',
              border: '1px solid rgba(231,76,60,0.4)',
              borderRadius: '10px',
              padding: '28px 32px',
              maxWidth: '480px', width: '100%',
              boxShadow: '0 8px 32px rgba(231,76,60,0.2)',
            }}>
            <div style={{ ...S.anton, fontSize: '18px', letterSpacing: '3px', color: '#e74c3c', marginBottom: '12px' }}>
              DELETE {confirmDelete.type.toUpperCase()}?
            </div>
            <div style={{ ...S.mono, fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', letterSpacing: '0.5px' }}>
              You are about to delete <strong style={{ color: '#fff' }}>{confirmDelete.item.name}</strong>.
            </div>
            {confirmDelete.item.usedIn.length > 0 && (
              <div style={{
                background: 'rgba(231,76,60,0.08)',
                border: '1px solid rgba(231,76,60,0.3)',
                borderRadius: '6px',
                padding: '12px 14px',
                marginBottom: '20px',
              }}>
                <div style={{ ...S.mono, fontSize: '10px', letterSpacing: '1px', color: '#e74c3c', marginBottom: '6px' }}>
                  WARNING — USED IN {confirmDelete.item.usedIn.length} SAVE{confirmDelete.item.usedIn.length > 1 ? 'S' : ''}:
                </div>
                <div style={{ ...S.mono, fontSize: '11px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
                  {confirmDelete.item.usedIn.map(s => s.slotName || ('Slot ' + s.slot)).join(' · ')}
                </div>
                <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(231,76,60,0.7)', marginTop: '8px', fontStyle: 'italic' }}>
                  Deleting may break these saves. They will load with missing data.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <ActionBtn variant="secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>CANCEL</ActionBtn>
              <ActionBtn variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'DELETING…' : 'DELETE PERMANENTLY'}
              </ActionBtn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Single custom item card ──────────────────────────────────────────
const CustomCard = ({ item, onDelete, onEdit }) => {
  const isUsed = item.usedIn && item.usedIn.length > 0;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '8px',
      padding: '14px 16px',
      transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{
          width: '40px', height: '40px',
          borderRadius: '4px',
          background: item.thumbBg ? 'url(' + item.thumbBg + ') center/cover' : 'linear-gradient(135deg, rgba(192,57,43,0.3), rgba(212,175,55,0.2))',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'rgba(255,255,255,0.6)',
          ...S.anton, fontSize: '12px',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          {item.thumb ? (
            <img src={item.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            (item.name || '?').slice(0, 3).toUpperCase()
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...S.anton, fontSize: '13px', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.name}
          </div>
          <div style={{ ...S.mono, fontSize: '9px', letterSpacing: '0.5px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
            {item.meta}
          </div>
        </div>
      </div>
      <div style={{
        ...S.mono, fontSize: '9px', letterSpacing: '0.5px',
        color: isUsed ? 'rgba(46,204,113,0.7)' : 'rgba(255,255,255,0.35)',
        background: isUsed ? 'rgba(46,204,113,0.06)' : 'rgba(255,255,255,0.02)',
        border: '1px solid ' + (isUsed ? 'rgba(46,204,113,0.2)' : 'rgba(255,255,255,0.06)'),
        borderRadius: '3px', padding: '4px 8px',
        display: 'inline-block',
        marginTop: '4px',
      }}>
        {isUsed ? ('USED IN: ' + item.usedIn.map(s => s.slotName || ('SLOT ' + s.slot)).join(', ').toUpperCase()) : 'NOT USED'}
      </div>
      <div style={{ display: 'flex', gap: '6px', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        {onEdit && (
          <button onClick={onEdit}
            style={{
              flex: 1,
              ...S.mono, fontSize: '9px', letterSpacing: '1.5px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.6)',
              padding: '6px 0', borderRadius: '3px',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)'; e.currentTarget.style.color = '#d4af37'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}>
            EDIT
          </button>
        )}
        <button onClick={onDelete}
          style={{
            flex: 1,
            ...S.mono, fontSize: '9px', letterSpacing: '1.5px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.6)',
            padding: '6px 0', borderRadius: '3px',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(231,76,60,0.6)'; e.currentTarget.style.color = '#e74c3c'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}>
          DELETE
        </button>
      </div>
    </div>
  );
};

// ── Main Settings overlay ────────────────────────────────────────────
export default function Settings({ onClose, onSeed, seeding, seedDone, gameData, onUpdateGame, onShowHofSequence, onShowLegal, onPatchNotes, onTicketsSeen }) {
  const [user] = useAuthState(auth);
  const accessStatus = useAccess(user, false);
  const isAdmin = accessStatus === 'admin';
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [slotName, setSlotName] = useState(gameData?.slotName || '');
  const [savingSlot, setSavingSlot] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0); // 0=closed 1=confirm 2=deleting
  const [deleteTyped, setDeleteTyped] = useState('');

  // Game preference toggles — persisted to Firestore users/{uid}.prefs
  const [prefs, setPrefs] = useState({ soundEffects: true, animations: true });

  const updatePref = async (key, val) => {
    const next = { ...prefs, [key]: val };
    setPrefs(next);
    if (user) {
      try {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          await updateDoc(userRef, { prefs: next, updatedAt: new Date().toISOString() });
        } else {
          await setDoc(userRef, { prefs: next, email: user.email, createdAt: new Date().toISOString() });
        }
      } catch (e) {
        console.error('Failed to save pref:', e);
      }
    }
  };

  // Custom data state
  const [customBrands, setCustomBrands] = useState([]);
  const [customGMs, setCustomGMs] = useState([]);
  const [customWrestlers, setCustomWrestlers] = useState([]);
  const [customRosters, setCustomRosters] = useState([]);
  const [gameSlots, setGameSlots] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);

  const fetchAll = async () => {
    if (!user) return;
    setLoadingCustom(true);
    try {
      // Brands
      const bSnap = await getDocs(query(collection(db, 'customBrands'), where('userId', '==', user.uid)));
      setCustomBrands(bSnap.docs.map(d => ({ ...d.data(), _docId: d.id })));

      // GMs
      const gSnap = await getDocs(query(collection(db, 'customGMs'), where('userId', '==', user.uid)));
      setCustomGMs(gSnap.docs.map(d => ({ ...d.data(), _docId: d.id })));

      // Custom rosters
      const rSnap = await getDocs(query(collection(db, 'customRosters'), where('userId', '==', user.uid)));
      setCustomRosters(rSnap.docs.map(d => ({ ...d.data(), _docId: d.id })));

      // Custom wrestlers (in officialWrestlers, private to this user)
      const wSnap = await getDocs(query(collection(db, 'officialWrestlers'), where('userId', '==', user.uid)));
      setCustomWrestlers(wSnap.docs.map(d => ({ ...d.data(), _docId: d.id })));

      // Game slots (for usage detection)
      const sSnap = await getDocs(query(collection(db, 'gameSlots'), where('userId', '==', user.uid)));
      setGameSlots(sSnap.docs.map(d => ({ ...d.data(), _docId: d.id })));

      // Profile name — read from Firestore first (overrides Firebase Auth)
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.displayName) {
            setDisplayName(data.displayName);
            setOriginalName(data.displayName);
          } else if (user.displayName) {
            setDisplayName(user.displayName);
            setOriginalName(user.displayName);
          } else if (user.email) {
            const emailName = user.email.split('@')[0];
            setDisplayName(emailName);
            setOriginalName(emailName);
          }
          // Load saved prefs
          if (data.prefs) {
            setPrefs(prev => ({ ...prev, ...data.prefs }));
          } else {
            // One-time migration from localStorage
            try {
              const raw = localStorage.getItem('gamePrefs');
              if (raw) {
                const parsed = JSON.parse(raw);
                setPrefs(prev => ({ ...prev, ...parsed }));
                await updateDoc(userRef, { prefs: parsed, updatedAt: new Date().toISOString() }).catch(() => {});
                localStorage.removeItem('gamePrefs');
              }
            } catch {}
          }
        } else if (user.displayName) {
          setDisplayName(user.displayName);
          setOriginalName(user.displayName);
        } else if (user.email) {
          const emailName = user.email.split('@')[0];
          setDisplayName(emailName);
          setOriginalName(emailName);
        }
      } catch {
        if (user.displayName) { setDisplayName(user.displayName); setOriginalName(user.displayName); }
      }
    } catch (e) {
      console.error('Failed to load custom data:', e);
    } finally {
      setLoadingCustom(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSaveDisplayName = async () => {
    if (!user || displayName === originalName) return;
    setSavingName(true);
    try {
      // 1. Save to Firestore users doc
      const ref = doc(db, 'users', user.uid);
      const existing = await getDoc(ref);
      if (existing.exists()) {
        await updateDoc(ref, { displayName, updatedAt: new Date().toISOString() });
      } else {
        await setDoc(ref, { displayName, email: user.email, createdAt: new Date().toISOString() });
      }
      // 2. Save to Firebase Auth profile
      try {
        const { updateProfile } = await import('firebase/auth');
        await updateProfile(user, { displayName });
      } catch {}
      setOriginalName(displayName);
    } catch (e) {
      alert('Failed to save: ' + e.message);
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveSlotName = async () => {
    if (!gameData?.id || !slotName.trim()) return;
    setSavingSlot(true);
    try {
      const { updateDoc: ud, doc: d } = await import('firebase/firestore');
      await ud(d(db, 'gameSlots', gameData.id), { slotName: slotName.trim() });
      if (onUpdateGame) onUpdateGame({ ...gameData, slotName: slotName.trim() });
    } catch (e) {
      alert('Failed: ' + e.message);
    } finally {
      setSavingSlot(false);
    }
  };

  const handleClearCache = () => {
    try {
      sessionStorage.clear();
      alert('Cache cleared. Refresh the page to reload data.');
    } catch (e) {
      alert('Failed to clear cache: ' + e.message);
    }
  };

  const handleExportSaves = () => {
    const exportData = {
      version: APP_VERSION,
      exportedAt: new Date().toISOString(),
      user: { uid: user.uid, email: user.email },
      gameSlots,
      customBrands,
      customGMs,
      customRosters,
      customWrestlers,
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    var brandSlug = (gameData?.brand || 'brand').replace(/\s+/g, '-').toUpperCase();
    var slotSlug = (gameData?.slotName || ('slot' + gameData?.slot) || 'save').replace(/\s+/g, '-').toUpperCase();
    var dateSlug = new Date().toISOString().slice(0, 10);
    a.download = 'GMRE_' + brandSlug + '_' + slotSlug + '_' + dateSlug + '.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = () => {
    setDeleteStep(1);
  };

  const handleConfirmDelete = async () => {
    setDeleteStep(2);
    try {
      const cols = ['gameSlots', 'customBrands', 'customGMs', 'customRosters'];
      for (const col of cols) {
        const snap = await getDocs(query(collection(db, col), where('userId', '==', user.uid)));
        for (const d of snap.docs) await deleteDoc(d.ref);
      }
      await deleteDoc(doc(db, 'users', user.uid)).catch(() => {});
      await user.delete();
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') {
        alert('For security, please sign out and sign back in before deleting your account.');
      } else {
        alert('Failed to delete account: ' + e.message);
      }
      setDeleteStep(0);
    }
  };

  if (!user) return null;

  const initial = (displayName || user.email || '?').slice(0, 1).toUpperCase();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: '#0a0a0a',
      overflowY: 'auto',
    }}>
      {/* BG decoration */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0,
        background:
          'radial-gradient(ellipse at 20% 30%, rgba(192,57,43,0.08) 0%, transparent 50%),' +
          'radial-gradient(ellipse at 80% 70%, rgba(212,175,55,0.05) 0%, transparent 50%),' +
          '#0a0a0a',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '880px', margin: '0 auto', padding: '60px 32px 60px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
          <div>
            <div style={{ ...S.anton, fontSize: '32px', letterSpacing: '6px', color: '#fff', textShadow: '0 0 20px rgba(212,175,55,0.2)' }}>
              SETTINGS
            </div>
            <div style={{ ...S.mono, fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>
              Configure your game preferences and manage your account
            </div>
          </div>
          <button onClick={onClose}
            style={{
              width: '36px', height: '36px',
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.7)',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(231,76,60,0.2)'; e.currentTarget.style.borderColor = 'rgba(231,76,60,0.6)'; e.currentTarget.style.color = '#e74c3c'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.5)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}>
            <IconClose />
          </button>
        </div>

        {/* 01 PROFILE */}
        <Section num="01" title="PROFILE">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{
              width: '56px', height: '56px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(192,57,43,0.4), rgba(212,175,55,0.3))',
              border: '2px solid rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              ...S.anton, fontSize: '22px', letterSpacing: '2px', color: '#fff',
              flexShrink: 0,
            }}>{initial}</div>
            <div>
              <div style={{ ...S.anton, fontSize: '18px', letterSpacing: '2px' }}>{displayName || 'Player'}</div>
              <div style={{ ...S.mono, fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginTop: '2px' }}>{user.email}</div>
            </div>
          </div>
          <Row label="Display name" desc="How your name appears in-game">
            <div style={{ display: 'flex', gap: '6px' }}>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: '#fff',
                  ...S.mono, fontSize: '12px',
                  padding: '7px 10px', borderRadius: '4px',
                  minWidth: '180px', outline: 'none',
                }} />
              <ActionBtn variant="secondary" onClick={handleSaveDisplayName} disabled={savingName || displayName === originalName}>
                {savingName ? 'SAVING…' : 'SAVE'}
              </ActionBtn>
            </div>
          </Row>
          {gameData && (
            <Row label="Save slot name" desc="Rename this save slot">
              <div style={{ display: 'flex', gap: '6px' }}>
                <input value={slotName} onChange={e => setSlotName(e.target.value)}
                  placeholder={gameData.slotName || ('Slot ' + gameData.slot)}
                  maxLength={20}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: '#fff',
                    ...S.mono, fontSize: '12px',
                    padding: '7px 10px', borderRadius: '4px',
                    minWidth: '180px', outline: 'none',
                  }} />
                <ActionBtn variant="secondary" onClick={handleSaveSlotName} disabled={savingSlot || !slotName.trim() || slotName.trim() === gameData.slotName}>
                  {savingSlot ? 'SAVING…' : 'SAVE'}
                </ActionBtn>
              </div>
            </Row>
          )}
          <Row label="Email" desc="Cannot be changed">
            <input value={user.email} readOnly
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.5)',
                ...S.mono, fontSize: '12px',
                padding: '7px 10px', borderRadius: '4px',
                minWidth: '220px', outline: 'none',
                cursor: 'not-allowed',
              }} />
          </Row>
        </Section>

        {/* 02 GAME PREFERENCES */}
        <Section num="02" title="GAME PREFERENCES">
          <Row label="Theme" desc="Light mode coming in a future update" comingSoon>
            <Toggle on={false} disabled />
          </Row>
          <Row label="Sound effects" desc="Play sound effects on key actions" comingSoon>
            <Toggle on={prefs.soundEffects} disabled />
          </Row>
          <Row label="Animations" desc="Trophy unlocks and victory animations" comingSoon>
            <Toggle on={prefs.animations} disabled />
          </Row>
        </Section>

        {/* 03 CUSTOM CONTENT */}
        <Section num="03" title="CUSTOM CONTENT" custom
          description="Manage everything you've created. Each card shows where the item is used. Deleting an item used in an active save may break that save's data — use with caution.">
          {loadingCustom ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.3)', fontSize: '11px', letterSpacing: '1px', ...S.mono }}>
              Loading custom content…
            </div>
          ) : (
            <CustomContentManager
              user={user}
              gameSlots={gameSlots}
              customBrands={customBrands}
              customGMs={customGMs}
              customWrestlers={customWrestlers}
              customRosters={customRosters}
              onRefresh={fetchAll}
            />
          )}
        </Section>

        {/* 04 DATA & ACCOUNT */}
        <Section num="04" title="DATA & ACCOUNT">
          <Row label="Update Wrestlers DB" desc="Refresh wrestler data from the server">
            <ActionBtn onClick={onSeed} disabled={seeding}>
              {seeding ? 'UPDATING…' : seedDone ? 'UP TO DATE' : 'UPDATE DB'}
            </ActionBtn>
          </Row>
          <Row label="Clear cache" desc="Force a full reload of cached data">
            <ActionBtn variant="secondary" onClick={handleClearCache}>CLEAR</ActionBtn>
          </Row>
          <Row label="Export game saves" desc="Download all your saves and customs as a JSON file">
            <ActionBtn variant="secondary" onClick={handleExportSaves}>EXPORT</ActionBtn>
          </Row>
        </Section>

        {/* 05 MY TICKETS */}
          <Section num="05" title="MY TICKETS" description="Track the status of bugs and issues you've reported.">
         <MyTicketsList user={user} onSeen={onTicketsSeen} />
        </Section>

        {/* 06 DEV TOOLS — admin only, visible only in-game */}
        {isAdmin && gameData && (
          <Section num="06" title="⚙ DEV TOOLS">
            <Row label="Active save" desc={gameData.slotName || ('Slot ' + gameData.slot) + ' — ' + (gameData.brand || '') + ' · Week ' + (gameData.week || 1)}>
              <ActionBtn onClick={() => setShowDevPanel(true)}>OPEN PANEL</ActionBtn>
            </Row>
          </Section>
        )}

        {/* 07 ABOUT */}
        <Section num={isAdmin && gameData ? '07' : '06'} title="ABOUT">
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px', padding: '6px 0', fontSize: '11px' }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '9px', ...S.mono }}>Version</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', ...S.mono }}>{APP_VERSION}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '9px', ...S.mono }}>Build</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', ...S.mono }}>{APP_BUILD}</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '9px', ...S.mono }}>Engine</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', ...S.mono }}>React 19 / Firebase 11</div>
            <div style={{ color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase', fontSize: '9px', ...S.mono }}>Creator</div>
            <div style={{ color: 'rgba(255,255,255,0.85)', ...S.mono }}>A THEIMMORTAL21 PROJECT</div>
          </div>
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: '10px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, letterSpacing: '0.5px', ...S.mono }}>
            Wrestler data and renders sourced from WWE 2K25 / 2K26 — used for personal, non-commercial purposes only.<br />
            Not affiliated with WWE, 2K, or Visual Concepts.
          </div>
          <div style={{ marginTop: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            {['cgu', 'privacy', 'disclaimer'].map(key => (
              <button key={key} onClick={() => onShowLegal && onShowLegal(key)}
                style={{ ...S.mono, fontSize: '9px', letterSpacing: '1.5px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', textTransform: 'uppercase', transition: 'border-color 0.15s, color 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}>
                {key === 'cgu' ? 'Terms of Service' : key === 'privacy' ? 'Privacy Policy' : 'Disclaimer'}
              </button>
            ))}
            {onPatchNotes && (
              <button
                onClick={() => { onClose(); onPatchNotes(); }}
                style={{ ...S.mono, fontSize: '9px', letterSpacing: '1.5px', background: 'transparent', border: '1px solid rgba(224,16,16,0.25)', color: 'rgba(224,16,16,0.6)', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', textTransform: 'uppercase', transition: 'border-color 0.15s, color 0.15s', display: 'flex', alignItems: 'center', gap: '6px' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(224,16,16,0.7)'; e.currentTarget.style.color = '#e01010'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(224,16,16,0.25)'; e.currentTarget.style.color = 'rgba(224,16,16,0.6)'; }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#e01010', display: 'inline-block', flexShrink: 0 }} />
                Patch Notes
              </button>
            )}
          </div>
        </Section>

        {/* 08 DANGER ZONE */}
        <Section num={isAdmin && gameData ? '08' : '07'} title="DANGER ZONE" danger>
          <Row label={<span style={{ color: '#e74c3c' }}>Delete account</span>} desc="Permanently delete your account and all your saves. This cannot be undone.">
            <ActionBtn variant="danger" onClick={handleDeleteAccount} disabled={deleteStep > 0}>
              {deleteStep === 2 ? 'DELETING…' : 'DELETE ACCOUNT'}
            </ActionBtn>
          </Row>
        </Section>
      </div>

      {/* Delete Account confirmation modal */}
      {deleteStep === 1 && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={() => { setDeleteStep(0); setDeleteTyped(''); }}>
          <div style={{ background: 'rgba(8,4,4,0.98)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '12px', padding: '32px', width: '100%', maxWidth: '420px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ ...S.anton, fontSize: '20px', letterSpacing: '4px', color: '#e74c3c', marginBottom: '12px' }}>DELETE ACCOUNT</div>
            <div style={{ ...S.mono, fontSize: '12px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: '8px' }}>
              This will permanently delete:
            </div>
            <div style={{ ...S.mono, fontSize: '11px', color: 'rgba(255,255,255,0.45)', lineHeight: 2, marginBottom: '20px', paddingLeft: '12px', borderLeft: '2px solid rgba(231,76,60,0.3)' }}>
              All game saves &amp; season history<br />
              All custom brands, GMs, rosters &amp; superstars<br />
              Your account credentials
            </div>
            <div style={{ ...S.mono, fontSize: '11px', color: 'rgba(231,76,60,0.8)', marginBottom: '16px' }}>
              This action is irreversible. There is no going back.
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ ...S.mono, fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>
                Type <span style={{ color: '#e74c3c', fontWeight: 700 }}>DELETE</span> to confirm
              </div>
              <input
                value={deleteTyped}
                onChange={e => setDeleteTyped(e.target.value)}
                placeholder="DELETE"
                style={{ ...S.mono, fontSize: '13px', letterSpacing: '2px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '4px', color: '#fff', padding: '8px 14px', width: '100%', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteStep(0); setDeleteTyped(''); }}
                style={{ ...S.mono, fontSize: '10px', letterSpacing: '1.5px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', padding: '8px 20px', borderRadius: '4px', cursor: 'pointer' }}>
                CANCEL
              </button>
              <button onClick={handleConfirmDelete} disabled={deleteTyped !== 'DELETE'}
                style={{ ...S.mono, fontSize: '10px', letterSpacing: '1.5px', background: deleteTyped === 'DELETE' ? 'rgba(231,76,60,0.15)' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (deleteTyped === 'DELETE' ? 'rgba(231,76,60,0.7)' : 'rgba(255,255,255,0.1)'), color: deleteTyped === 'DELETE' ? '#e74c3c' : 'rgba(255,255,255,0.2)', padding: '8px 20px', borderRadius: '4px', cursor: deleteTyped === 'DELETE' ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}>
                YES, DELETE EVERYTHING
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Dev Panel modal */}
      {showDevPanel && gameData && isAdmin && (
        <AdminDevPanel
          gameData={gameData}
          slotId={gameData.id}
          onSave={(updated) => {
            onUpdateGame && onUpdateGame(updated);
            setShowDevPanel(false);
          }}
          onClose={() => setShowDevPanel(false)}
          onShowHofSequence={onShowHofSequence}
        />
      )}
    </div>
  );
}
